import { Link, useNavigate } from 'react-router-dom';

interface EventHeroProps {
  title: string;
  category: string;
  coverImage: string | null;
  coverImagePosition?: string;
  organizerName: string;
  startDate: string;
  alreadyRegistered?: boolean;
  eventId?: string;
  price?: number | null;
  currency?: string | null;
  isFull?: boolean;
  onRegisterClick?: () => void;
}

export default function EventHero({
  title,
  category,
  coverImage,
  coverImagePosition = 'center',
  organizerName,
  startDate,
  alreadyRegistered = false,
  eventId,
  price,
  currency,
  isFull = false,
  onRegisterClick,
}: EventHeroProps) {
  const navigate = useNavigate();

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const copyLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleHeroRegister = () => {
    if (onRegisterClick) {
      onRegisterClick();
    } else if (eventId) {
      navigate(`/event/${eventId}/register`);
    }
  };

  return (
    <section className="relative w-full h-[340px] sm:h-[400px] md:h-[480px] overflow-hidden mt-16 sm:mt-20">
      {coverImage ? (
        <img
          src={coverImage}
          alt={title}
          className="w-full h-full object-cover transition-all duration-300"
          style={{ objectPosition: coverImagePosition }}
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
          <i className="ri-calendar-event-line text-5xl sm:text-6xl md:text-7xl text-gray-600"></i>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/20"></div>

      {/* Back button */}
      <div className="absolute top-4 sm:top-6 left-4 sm:left-6 z-10">
        <Link
          to="/discover"
          className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/10 backdrop-blur-md text-white text-xs sm:text-sm font-medium rounded-lg hover:bg-white/20 transition-all cursor-pointer"
        >
          <i className="ri-arrow-left-line"></i>
          <span className="hidden sm:inline">Back to Events</span>
          <span className="sm:hidden">Back</span>
        </Link>
      </div>

      {/* Share button */}
      <div className="absolute top-4 sm:top-6 right-4 sm:right-6 z-10">
        <button
          onClick={copyLinkToClipboard}
          className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-white/10 backdrop-blur-md text-white rounded-lg hover:bg-white/20 transition-all cursor-pointer"
          title="Copy link"
        >
          <i className="ri-share-line text-base sm:text-lg"></i>
        </button>
      </div>

      {/* Hero content */}
      <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8 lg:p-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
            <span className="px-2.5 sm:px-3 py-1 bg-teal-500 text-white text-xs font-semibold rounded-full uppercase tracking-wide whitespace-nowrap">
              {category}
            </span>
            {alreadyRegistered && (
              <span className="px-2.5 sm:px-3 py-1 bg-emerald-500 text-white text-xs font-semibold rounded-full whitespace-nowrap flex items-center gap-1.5 animate-[fadeIn_0.3s_ease-out]">
                <i className="ri-checkbox-circle-fill text-sm"></i>
                Already Registered
              </span>
            )}
            <span className="px-2.5 sm:px-3 py-1 bg-white/15 backdrop-blur-sm text-white text-xs font-medium rounded-full whitespace-nowrap">
              <i className="ri-calendar-line mr-1"></i>
              <span className="hidden sm:inline">{formatDate(startDate)}</span>
              <span className="sm:hidden">{new Date(startDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
            </span>
          </div>
          <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold text-white leading-tight mb-2 sm:mb-3 max-w-3xl">
            {title}
          </h1>
          <p className="text-gray-300 text-xs sm:text-sm flex items-center gap-2 mb-4 sm:mb-0">
            <span className="w-5 h-5 sm:w-6 sm:h-6 bg-teal-500/30 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-user-line text-teal-300 text-xs"></i>
            </span>
            <span className="truncate">Hosted by <strong className="text-white">{organizerName}</strong></span>
          </p>

          {/* Mobile-only CTA — visible immediately on page load */}
          {!alreadyRegistered && !isFull && (
            <div className="sm:hidden mt-4 flex items-center gap-3">
              <button
                onClick={handleHeroRegister}
                className="flex-1 py-3 bg-gradient-to-r from-teal-500 via-emerald-500 to-cyan-500 text-white font-bold text-sm rounded-xl active:scale-[0.98] transition-all whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-ticket-line text-base"></i>
                Register Now
                {price && price > 0 ? (
                  <span className="bg-white/20 rounded-lg px-2 py-0.5 text-xs font-semibold">
                    {currency || 'USD'} {price.toFixed(2)}
                  </span>
                ) : (
                  <span className="bg-white/20 rounded-lg px-2 py-0.5 text-xs font-semibold">Free</span>
                )}
              </button>
            </div>
          )}

          {alreadyRegistered && (
            <div className="sm:hidden mt-4">
              <div className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-500/90 backdrop-blur-sm text-white text-sm font-semibold rounded-xl">
                <i className="ri-checkbox-circle-fill text-base"></i>
                You&apos;re registered!
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
